import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SelectButton } from 'primereact/selectbutton';
import { InputText } from 'primereact/inputtext';
import { Calendar } from '../../components/calendar';

import { pesquisarProdutos, mensagemErro } from '../tributosApi';
import { SituacaoProdutoTag, formatarNcm, dateToIso } from '../util';

const OPCOES_CONFERIDO = [
  { label: 'Todos', value: 'T' },
  { label: 'Conferidos', value: 'S' },
  { label: 'Não conferidos', value: 'N' },
];

export default function Produtos() {
  const navigate = useNavigate();
  const [codigoEan, setCodigoEan] = useState('');
  const [descricao, setDescricao] = useState('');
  const [ncm, setNcm] = useState('');
  const [cest, setCest] = useState('');
  const [periodoConferencia, setPeriodoConferencia] = useState(null);
  const [conferido, setConferido] = useState('T');
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    try {
      const soDigitos = v => String(v || '').replace(/\D/g, '');
      const inicio = periodoConferencia && periodoConferencia[0];
      const fim = periodoConferencia && (periodoConferencia[1] || periodoConferencia[0]);
      const params = {
        codigoEan: codigoEan.trim() || undefined,
        descricao: descricao.trim() || undefined,
        ncm: soDigitos(ncm) || undefined,
        cest: soDigitos(cest) || undefined,
        conferido: conferido === 'T' ? undefined : conferido === 'S',
        dataConferenciaInicial: inicio ? dateToIso(inicio) : undefined,
        dataConferenciaFinal: fim ? dateToIso(fim) : undefined,
      };
      setLista(await pesquisarProdutos(params));
    } catch (e) {
      toast.error(mensagemErro(e, 'Erro ao consultar produtos.'));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conferido]);

  const pesquisar = e => {
    e.preventDefault();
    carregar();
  };

  const limpar = () => {
    setCodigoEan('');
    setDescricao('');
    setNcm('');
    setCest('');
    setPeriodoConferencia(null);
  };

  const colunaIcms = p => (
    <div className="tb-cell-sub tb-mono">
      <span>{p.cstVenda ? `CST ${p.cstVenda}` : ''}{p.cstVenda && p.csosnVenda ? ' · ' : ''}{p.csosnVenda ? `CSOSN ${p.csosnVenda}` : ''}{!p.cstVenda && !p.csosnVenda ? '—' : ''}</span>
      <small>{p.icmsAliquota ? `${Number(p.icmsAliquota).toLocaleString('pt-BR')}%` : ''}{p.cfopVenda ? ` · CFOP ${p.cfopVenda}` : ''}</small>
    </div>
  );

  return (
    <div className="tb-page">
      <div className="tb-page-head">
        <div>
          <h1>Base de tributos</h1>
          <p>Cadastro fiscal dos produtos por código EAN.</p>
        </div>
        <button type="button" className="tb-btn tb-btn-primary" onClick={() => navigate('/tributos/produtos/novo')}>
          <i className="pi pi-plus" /> Novo produto
        </button>
      </div>

      <section className="tb-card">
        <form className="tb-filters" onSubmit={pesquisar}>
          <SelectButton value={conferido} options={OPCOES_CONFERIDO} onChange={e => e.value && setConferido(e.value)} className="tb-segmented" />
          <div className="tb-filters-row">
            <span className="p-input-icon-left tb-filtro-ean">
              <i className="pi pi-qrcode" />
              <InputText value={codigoEan} onChange={e => setCodigoEan(e.target.value)} placeholder="EAN" />
            </span>
            <span className="p-input-icon-left tb-grow">
              <i className="pi pi-search" />
              <InputText value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Descrição" />
            </span>
            <InputText className="tb-filtro-curto" value={ncm} onChange={e => setNcm(e.target.value)} placeholder="NCM" maxLength={10} />
            <InputText className="tb-filtro-curto" value={cest} onChange={e => setCest(e.target.value)} placeholder="CEST" maxLength={9} />
            <Calendar
              value={periodoConferencia}
              onChange={e => setPeriodoConferencia(e.value)}
              selectionMode="range"
              dateFormat="dd/mm/yy"
              readOnlyInput
              showIcon
              showButtonBar
              placeholder="Conferido em"
              className="tb-filtro-data"
            />
            <button type="button" className="tb-btn tb-btn-ghost" onClick={limpar} title="Limpar filtros" aria-label="Limpar filtros"><i className="pi pi-filter-slash" /></button>
            <button type="submit" className="tb-btn tb-btn-primary"><i className="pi pi-filter" /> Pesquisar</button>
          </div>
        </form>

        <DataTable
          value={lista}
          loading={carregando}
          paginator
          rows={25}
          rowsPerPageOptions={[25, 50, 100]}
          dataKey="id"
          responsiveLayout="stack"
          breakpoint="900px"
          emptyMessage="Nenhum produto encontrado."
          className="tb-table"
          onRowDoubleClick={e => navigate(`/tributos/produtos/${e.data.id}`)}
        >
          <Column header="EAN" body={p => p.codigoEan || <span className="tb-sem-ean" title={`CNPJ ${p.cnpjCliente || ''}`}>Reg. {p.registroProduto || '—'}</span>} bodyClassName="tb-mono" style={{ width: '11rem' }} />
          <Column header="Descrição" field="descricao" />
          <Column header="NCM" body={p => <span className="tb-mono">{p.ncm ? formatarNcm(p.ncm) : '—'}</span>} style={{ width: '9rem' }} />
          <Column header="ICMS" body={colunaIcms} style={{ width: '12rem' }} />
          <Column header="Situação" body={p => <SituacaoProdutoTag produto={p} />} style={{ width: '11rem' }} />
          <Column header="Conferido em" field="dataConferencia" style={{ width: '9rem' }} />
          <Column
            body={p => (
              <button type="button" className="tb-btn tb-btn-ghost tb-btn-sm" onClick={() => navigate(`/tributos/produtos/${p.id}`)}>
                <i className="pi pi-pencil" /> Editar
              </button>
            )}
            style={{ width: '7rem' }}
          />
        </DataTable>
      </section>
    </div>
  );
}
